import { BpsLogPoint, ChartPoint } from "./types";
import { formatTimeLabel } from "./datetime";

export const GAP_THRESHOLD_MS = 3_000; // 3秒以上空いたら「途切れ」とみなす

const computeTypicalIntervalMs = (data: BpsLogPoint[]): number | null => {
  if (!Array.isArray(data) || data.length < 2) return null;

  const deltas: number[] = [];
  for (let i = 0; i + 1 < data.length; i++) {
    const t0 = new Date(data[i].timestamp).getTime();
    const t1 = new Date(data[i + 1].timestamp).getTime();
    if (!Number.isFinite(t0) || !Number.isFinite(t1)) continue;
    const d = t1 - t0;
    if (!Number.isFinite(d) || d <= 0) continue;
    deltas.push(d);
  }

  if (deltas.length === 0) return null;
  deltas.sort((a, b) => a - b);
  return deltas[Math.floor(deltas.length / 2)] ?? null;
};

// 期間指定表示用に、ログ点をチャート点へ変換し、途切れ(gap)表現のnull点も挿入する。
export const buildPeriodChartPoints = (
  bpslogDataForDisplay: BpsLogPoint[],
  rangeStartLocal?: string,
  rangeEndLocal?: string,
  gapThresholdMs: number = GAP_THRESHOLD_MS,
): ChartPoint[] => {
  const parseLocalToMs = (v?: string) => {
    if (!v) return null;
    const ms = new Date(v).getTime();
    return Number.isFinite(ms) ? ms : null;
  };

  const startMsRaw = parseLocalToMs(rangeStartLocal);
  const endMsRaw = parseLocalToMs(rangeEndLocal);
  const hasExplicitRange = startMsRaw !== null && endMsRaw !== null;
  const rangeStartMs = hasExplicitRange ? Math.min(startMsRaw, endMsRaw) : null;
  const rangeEndMs = hasExplicitRange ? Math.max(startMsRaw, endMsRaw) : null;

  const rangeMs =
    typeof rangeStartMs === "number" && typeof rangeEndMs === "number"
      ? rangeEndMs - rangeStartMs
      : bpslogDataForDisplay.length >= 2
        ? new Date(
            bpslogDataForDisplay[bpslogDataForDisplay.length - 1].timestamp,
          ).getTime() - new Date(bpslogDataForDisplay[0].timestamp).getTime()
        : 0;

  const showDate = rangeMs >= 6 * 60 * 60 * 1000; // 6時間以上のレンジなら日付も付ける

  const chartPoints: ChartPoint[] = [];

  // ログ間隔が数秒〜数十秒の場合、固定 3s で gap を作ると常に線が切れてしまう。
  // 典型間隔(中央値)の数倍より大きい場合のみ gap とみなす。
  const typicalIntervalMs = computeTypicalIntervalMs(bpslogDataForDisplay);
  const effectiveGapThresholdMs =
    typeof typicalIntervalMs === "number" && Number.isFinite(typicalIntervalMs)
      ? Math.max(gapThresholdMs, Math.floor(typicalIntervalMs * 3))
      : gapThresholdMs;

  for (let i = 0; i < bpslogDataForDisplay.length; i++) {
    const cur = bpslogDataForDisplay[i];
    chartPoints.push({
      label: formatTimeLabel(cur.timestamp, showDate),
      originalIdx: i,
      timestamp: cur.timestamp,
      xMs: (() => {
        const ms = new Date(cur.timestamp).getTime();
        return Number.isFinite(ms) ? ms : 0;
      })(),
      l1: cur.line1_bitrate ?? null,
      l2: cur.line2_bitrate ?? null,
      l3: cur.line3_bitrate ?? null,
      l4: cur.line4_bitrate ?? null,
      actual: cur.actual_bitrate ?? null,
    });

    const next =
      i + 1 < bpslogDataForDisplay.length ? bpslogDataForDisplay[i + 1] : null;
    if (!next) continue;
    const t0 = new Date(cur.timestamp).getTime();
    const t1 = new Date(next.timestamp).getTime();
    if (!Number.isFinite(t0) || !Number.isFinite(t1)) continue;
    if (t1 - t0 >= effectiveGapThresholdMs) {
      // null点を挿入して線を切る
      chartPoints.push({
        label: "",
        originalIdx: null,
        timestamp: new Date(t0 + 1).toISOString(),
        xMs: t0 + 1,
        l1: null,
        l2: null,
        l3: null,
        l4: null,
        actual: null,
      });
    }
  }

  return chartPoints;
};
